import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";

/** What the button is for, which is what decides how it looks. */
export type Variant = "primary" | "secondary" | "plain";

/** How wide it is. `lg` is full width; the other two are fixed. */
export type Size = "lg" | "md" | "sm";

/**
 * The global class each choice maps to.
 *
 * The declarations stay in App.scss, where the rest of the application can
 * still reach them: `header.module.scss` trims `:global(.primary-btn)` to 46px,
 * and the driver upload screens size their label-and-button pair against these
 * heights. What moves here is the choice, not the CSS. Sixty call sites spelled
 * these out as free text in twelve different ways, four with a trailing space
 * and ten with the two words in the other order.
 */
const VARIANT_CLASS: Record<Variant, string> = {
    primary: "primary-btn",
    secondary: "secondary-btn",
    /** No variant styling, for a button a screen styles entirely itself. */
    plain: "",
};

const SIZE_CLASS: Record<Size, string> = {
    lg: "btn-lg",
    md: "btn-md",
    sm: "btn-sm",
};

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "content"> & {
    variant?: Variant;
    size?: Size;
    /** Anything else: a module class, or a one-off utility. */
    classes?: string;
    content: ReactNode;
    onClick?: () => void;
    loading?: boolean;
    disabled?: boolean;
    icon?: ReactNode;
    title?: string;
    /** An external link. Renders an anchor inside the button. */
    url?: string;
    /** An in-app route. Renders a router Link inside the button. */
    route?: string;
    style?: CSSProperties;
};

/**
 * The application button.
 *
 * The click animation is applied to the element that was clicked, through its
 * own handler. The previous implementation ran, on every single render of every
 * button in the tree, a `document.getElementsByClassName("btn")` sweep that
 * attached a click listener to every button on the page. Nothing ever removed
 * them, so a screen with eight buttons that re-rendered thirty times ended up
 * with hundreds of listeners on each, and each click ran the animation once per
 * listener. That is gone.
 */
const Button = ({
    variant,
    size,
    classes,
    onClick,
    loading,
    content,
    disabled,
    icon,
    title,
    url,
    route,
    type = "submit",
    style,
    ...rest
}: Props) => {
    const animate = (element: HTMLElement) => {
        element.classList.remove("animate");
        // Reading offsetWidth forces a reflow, which is what makes removing and
        // re-adding the class in the same tick restart the animation rather
        // than being coalesced into no change at all.
        void element.offsetWidth;
        element.classList.add("animate");
        window.setTimeout(() => element.classList.remove("animate"), 700);
    };

    return (
        <button
            style={style}
            type={type}
            title={title}
            className={[
                "btn",
                variant ? VARIANT_CLASS[variant] : "",
                size ? SIZE_CLASS[size] : "",
                classes ?? "",
            ]
                .filter(Boolean)
                .join(" ")}
            onClick={(event) => {
                animate(event.currentTarget);
                onClick?.();
            }}
            disabled={loading ?? disabled}
            {...rest}
        >
            {route ? <Link to={route}>{content}</Link> : null}
            {url ? <a href={url}>{content}</a> : null}
            {!route && !url && content}
            {icon ? <span>{icon}</span> : null}
            {loading ? (
                <span className="fas fa-spinner fa-pulse text-white" />
            ) : null}
        </button>
    );
};

export default Button;
